/**
 * Grouping expiries for the picker.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHICH ONE IS MONTHLY IS DERIVED, NOT LABELLED.
 *
 * The catalogue gives dates, not a weekly/monthly flag. An expiry is the
 * MONTHLY one when no later expiry falls in the same calendar month — which
 * is the definition the exchange uses, and it stays correct when a holiday
 * moves an expiry off its usual weekday.
 *
 * Its one soft edge: for the LAST month in the list we cannot know whether
 * the exchange will list a later date in it, so the last known expiry of a
 * trailing month is marked monthly. It corrects itself as soon as the later
 * contract is listed, and the alternative — marking nothing — would leave
 * the far months unlabelled every day.
 *
 * Pure, and separated from the component, because this is the part that
 * would be wrong in a way nobody notices until a month end.
 * ─────────────────────────────────────────────────────────────────────────────
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export interface ExpiryItem {
  /** The ISO date, as the API keys on. */
  iso: string
  /** "13 Oct". */
  day: string
  /** "Tue". */
  dow: string
  /** Whole days from today, floored. Never negative. */
  dte: number
  monthly: boolean
  /** "OCT '26", the group heading. */
  groupKey: string
}

export interface ExpiryGroup {
  label: string
  items: ExpiryItem[]
}

/** Parse `YYYY-MM-DD` as a LOCAL date — not UTC, which shifts the day. */
function parseIso(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!m) return null
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
}

export function buildExpiries(isoDates: readonly string[], now: Date = new Date()): ExpiryItem[] {
  const parsed = isoDates
    .map((iso) => ({ iso, date: parseIso(iso) }))
    .filter((x): x is { iso: string; date: Date } => x.date !== null)
    .sort((a, b) => a.date.getTime() - b.date.getTime())

  // The last expiry in each calendar month is that month's monthly one.
  const lastOfMonth = new Map<string, string>()
  for (const { iso, date } of parsed) {
    lastOfMonth.set(`${date.getFullYear()}-${date.getMonth()}`, iso)
  }

  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()

  return parsed.map(({ iso, date }) => {
    const monthKey = `${date.getFullYear()}-${date.getMonth()}`
    return {
      iso,
      day: `${String(date.getDate()).padStart(2, '0')} ${MONTHS[date.getMonth()]}`,
      dow: DOW[date.getDay()] ?? '',
      // Floored and clamped: an expiry earlier today is 0 days away, never -1.
      dte: Math.max(0, Math.round((date.getTime() - midnight) / 86_400_000)),
      monthly: lastOfMonth.get(monthKey) === iso,
      groupKey: `${MONTHS[date.getMonth()]?.toUpperCase()} '${String(date.getFullYear()).slice(2)}`,
    }
  })
}

/** Month groups, in order, each keeping its own order. */
export function groupExpiries(items: readonly ExpiryItem[]): ExpiryGroup[] {
  const groups: ExpiryGroup[] = []
  for (const item of items) {
    let g = groups.find((x) => x.label === item.groupKey)
    if (!g) {
      g = { label: item.groupKey, items: [] }
      groups.push(g)
    }
    g.items.push(item)
  }
  return groups
}
